import React, { useState } from 'react';
import { Volume2, VolumeX, Music, Music2, Gamepad2, Languages } from 'lucide-react';
import { getAudioConfig, setAudioConfig, sfx } from './oilSounds';

const Row = ({ children }) => (
  <div className="rounded-lg border border-zinc-800 bg-black/50 px-2.5 py-2">{children}</div>
);

const Slider = ({ value, disabled, testid, onChange }) => (
  <input
    type="range"
    min="0"
    max="100"
    step="5"
    disabled={disabled}
    data-testid={testid}
    value={Math.round(value * 100)}
    onChange={(e) => onChange(Number(e.target.value) / 100)}
    className={`mt-1.5 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-zinc-700 accent-amber-400 ${disabled ? 'opacity-40' : ''}`}
    style={{ accentColor: '#fbbf24' }}
  />
);

export const OilSettingsMenu = ({ onExit, footer, locale, localeOptions, onLocale }) => {
  const [cfg, setCfg] = useState(getAudioConfig);
  const apply = (patch, tick = true) => {
    const next = setAudioConfig(patch);
    setCfg(next);
    if (tick) sfx.tick();
  };

  return (
    <div className="w-60 space-y-2" data-testid="oil-audio-settings">
      <p className="text-[9px] font-black uppercase tracking-[0.2em] text-amber-500">⚙️ Configurações</p>

      {localeOptions?.length > 1 && (
        <Row>
          <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-zinc-200">
            <Languages className="h-3.5 w-3.5 text-amber-400" /> Idioma do jogo
          </p>
          <div className="space-y-1" data-testid="oil-locale-picker">
            {localeOptions.map((opt) => (
              <button key={opt.code} onClick={() => onLocale?.(opt.code)}
                data-testid={`oil-locale-${opt.code}`}
                className={`flex w-full items-center justify-between rounded-lg border px-2 py-1.5 text-xs font-bold transition-colors ${
                  locale === opt.code
                    ? 'border-amber-500 bg-amber-500/15 text-amber-300'
                    : 'border-zinc-800 bg-black/50 text-zinc-300 hover:border-amber-700'}`}>
                {opt.label}
                {locale === opt.code && <span className="text-[10px]">✓</span>}
              </button>
            ))}
          </div>
        </Row>
      )}

      {/* MUDO GERAL */}
      <button
        onClick={() => apply({ muted: !cfg.muted }, cfg.muted)}
        data-testid="oil-mute-toggle"
        className={`flex w-full items-center justify-between rounded-lg border px-2.5 py-2 transition-colors ${
          cfg.muted ? 'border-red-700/80 bg-red-950/60' : 'border-zinc-800 bg-black/50'
        }`}
      >
        <span className="flex items-center gap-1.5 text-xs font-bold text-zinc-200">
          {cfg.muted ? <VolumeX className="h-3.5 w-3.5 text-red-400" /> : <Volume2 className="h-3.5 w-3.5 text-emerald-400" />}
          Mudo
        </span>
        <span className={`text-[10px] font-black ${cfg.muted ? 'text-red-400' : 'text-zinc-500'}`}>
          {cfg.muted ? 'TUDO MUDO' : 'DESLIGADO'}
        </span>
      </button>

      {/* SONS DO JOGO */}
      <Row>
        <button
          onClick={() => apply({ sfxOn: !cfg.sfxOn }, !cfg.sfxOn)}
          data-testid="oil-sound-toggle"
          className="flex w-full items-center justify-between"
        >
          <span className="flex items-center gap-1.5 text-xs font-bold text-zinc-200">
            <Gamepad2 className="h-3.5 w-3.5 text-amber-400" />
            Sons do jogo
          </span>
          <span className={`text-[10px] font-black ${cfg.sfxOn && !cfg.muted ? 'text-emerald-400' : 'text-zinc-500'}`}>
            {cfg.sfxOn ? '🔊 LIGADO' : '🔇 DESLIGADO'}
          </span>
        </button>
        <Slider
          value={cfg.sfxVol}
          disabled={!cfg.sfxOn || cfg.muted}
          testid="oil-sfx-volume"
          onChange={(v) => apply({ sfxVol: v })}
        />
      </Row>

      {/* MÚSICA */}
      <Row>
        <button
          onClick={() => apply({ musicOn: !cfg.musicOn }, false)}
          data-testid="oil-music-toggle"
          className="flex w-full items-center justify-between"
        >
          <span className="flex items-center gap-1.5 text-xs font-bold text-zinc-200">
            {cfg.musicOn ? <Music className="h-3.5 w-3.5 text-amber-400" /> : <Music2 className="h-3.5 w-3.5 text-zinc-500" />}
            Música
          </span>
          <span className={`text-[10px] font-black ${cfg.musicOn && !cfg.muted ? 'text-emerald-400' : 'text-zinc-500'}`}>
            {cfg.musicOn ? '🎵 LIGADA' : '🔇 DESLIGADA'}
          </span>
        </button>
        <Slider
          value={cfg.musicVol}
          disabled={!cfg.musicOn || cfg.muted}
          testid="oil-music-volume"
          onChange={(v) => apply({ musicVol: v }, false)}
        />
      </Row>

      <button
        onClick={onExit}
        data-testid="oil-back-btn"
        className="flex w-full items-center justify-center gap-1 rounded-lg border border-red-800/70 bg-red-950/50 px-3 py-2 text-xs font-black text-red-400 transition-transform active:scale-[0.98]"
      >
        🚪 Sair do jogo
      </button>

      {footer}
    </div>
  );
};

export default OilSettingsMenu;
